import type { McpCallSortKey, McpToolCall } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import type { Language } from "@/lib/i18n/language";
import { ISSUE_KINDS, issueKindLabel } from "@/lib/issues";
import { labelOrder } from "@/lib/table-sort";
import { toolSortOrder, type ToolTitles } from "@/lib/tool-titles";

/** A call that worked, in a sort by status, beside the failures' error kinds. */
const CALL_OK = "ok";

/**
 * Tools and error kinds the calls may hold besides those the dashboard knows
 * (the catalogue's and its own texts'), such as those of the past quarter's
 * calls: a tool DC Data no longer serves, a roadmap rule's refusal, an
 * exception's class name.
 */
export interface SeenCallValues {
  tools: readonly string[];
  errorKinds: readonly string[];
}

/** Nothing seen besides what the dashboard knows. */
export const NOTHING_SEEN: SeenCallValues = { tools: [], errorKinds: [] };

/**
 * What a call sorts on by status, as the server sorts it: `ok` for a call
 * that worked, else its error kind; null for a failure without one.
 */
export function callStatus(call: Pick<McpToolCall, "ok" | "error_type">): string | null {
  return call.ok ? CALL_OK : (call.error_type ?? null);
}

/**
 * A call's status in the Status column's words: "OK", an error kind's words
 * ("Odoo te traag"), or "Fout" for a failure without a kind.
 */
export function callStatusLabel(status: string | null): string {
  if (status === CALL_OK) {
    return i18n.t("analytics.ok");
  }
  return status ? issueKindLabel(status) : i18n.t("analytics.error");
}

/**
 * The order the server follows for a sort on the call log's Tool or Status
 * column (`sortOrder`): the tools (every tool the titles know, and those
 * seen) or the statuses (`ok`, every error kind the texts name, and those
 * seen) in the order of the words the table shows for them in `language`.
 * Undefined for the other columns, which sort on their own values.
 */
export function callSortOrder(
  key: McpCallSortKey | undefined,
  titles: ToolTitles,
  seen: SeenCallValues,
  language: Language,
): string[] | undefined {
  switch (key) {
    case "tool":
      return toolSortOrder(titles, seen.tools, language);
    case "status":
      return labelOrder([CALL_OK, ...ISSUE_KINDS, ...seen.errorKinds], callStatusLabel, language);
    default:
      return undefined;
  }
}
